import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FileText, Plus, Trash2, Save, Send, Eye, X,
} from "lucide-react";
import { Form, Modal } from "react-bootstrap";
import { PageHeader, AppButton, BackButton } from "../../ui";
import CommonToaster from "../../Common/CommonToaster";
import apiClient from "../../../utils/apiClient";
import "./service-invoices.scss";

const SERVICE_TYPES = [
  { value: "custom_development", label: "Custom Development" },
  { value: "integration", label: "Integration" },
  { value: "support", label: "Support" },
  { value: "amc", label: "AMC" },
  { value: "training", label: "Training" },
  { value: "others", label: "Others" },
];

const EMPTY_ITEM = { description: "", quantity: 1, unitPrice: 0 };

const ServiceInvoiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [institutes, setInstitutes] = useState([]);
  const [form, setForm] = useState({
    instituteId: "",
    branchId: "",
    serviceRequestId: "",
    serviceType: "custom_development",
    billingType: "one-time",
    notes: "",
    dueDate: "",
    lineItems: [{ ...EMPTY_ITEM }],
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [existingInvoice, setExistingInvoice] = useState(null);
  const [billingSettings, setBillingSettings] = useState({ enableGst: true, gstRate: 18 });
  const [showSendPreview, setShowSendPreview] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [draftInvoiceId, setDraftInvoiceId] = useState(null);
  const [sending, setSending] = useState(false);

  // Fetch institutes + billing settings
  useEffect(() => {
    const fetchInstitutes = async () => {
      try {
        const { data } = await apiClient.get("/sa/institutes");
        setInstitutes(data.institutes || data || []);
      } catch {
        // non-critical
      }
    };
    const fetchBilling = async () => {
      try {
        const { data } = await apiClient.get("/sa/settings");
        if (data.settings?.billing) setBillingSettings(data.settings.billing);
      } catch { /* defaults */ }
    };
    fetchInstitutes();
    fetchBilling();
  }, []);

  // Fetch invoice if editing
  useEffect(() => {
    if (!isEdit) return;
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const { data } = await apiClient.get(`/sa/service-invoices/${id}`);
        const inv = data.invoice;
        setExistingInvoice(inv);
        setForm({
          instituteId: inv.instituteId?._id || inv.instituteId || "",
          branchId: inv.branchId || "",
          serviceRequestId: inv.serviceRequestId || "",
          serviceType: inv.serviceType || "custom_development",
          billingType: inv.billingType || "one-time",
          notes: inv.notes || "",
          dueDate: inv.dueDate ? new Date(inv.dueDate).toISOString().slice(0, 10) : "",
          lineItems: inv.lineItems?.length
            ? inv.lineItems.map((li) => ({
                description: li.description,
                quantity: li.quantity,
                unitPrice: li.unitPrice,
              }))
            : [{ ...EMPTY_ITEM }],
        });
      } catch {
        setToast({ show: true, message: "Failed to load invoice.", type: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLineItemChange = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.lineItems];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, lineItems: items };
    });
  };

  const addLineItem = () => {
    setForm((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, { ...EMPTY_ITEM }],
    }));
  };

  const removeLineItem = (index) => {
    if (form.lineItems.length <= 1) return;
    setForm((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index),
    }));
  };

  const totalAmount = form.lineItems.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0),
    0
  );

  const gstEnabled = billingSettings.enableGst !== false;
  const gstRate = Number(billingSettings.gstRate) || 18;
  const halfRate = gstRate / 2;
  const gstAmount = gstEnabled && gstRate > 0 ? +(totalAmount * gstRate / 100).toFixed(2) : 0;
  const exactTotal = totalAmount + gstAmount;
  const grandTotal = Math.round(exactTotal);
  const roundedOff = +(grandTotal - exactTotal).toFixed(2);

  const validate = () => {
    if (!form.instituteId) return "Please select an institute.";
    if (!form.serviceType) return "Please select a service type.";
    if (!form.dueDate) return "Please set a due date.";
    if (!form.lineItems.length) return "Add at least one line item.";
    for (let i = 0; i < form.lineItems.length; i++) {
      const item = form.lineItems[i];
      if (!item.description?.trim()) return `Line item ${i + 1}: Description is required.`;
      if (!item.quantity || item.quantity < 1) return `Line item ${i + 1}: Quantity must be at least 1.`;
      if (item.unitPrice < 0) return `Line item ${i + 1}: Unit price cannot be negative.`;
    }
    return null;
  };

  const handleSubmit = async (sendImmediately = false) => {
    const error = validate();
    if (error) {
      setToast({ show: true, message: error, type: "error" });
      return;
    }

    const payload = {
      ...form,
      lineItems: form.lineItems.map((li) => ({
        description: li.description,
        quantity: Number(li.quantity),
        unitPrice: Number(li.unitPrice),
      })),
      status: sendImmediately ? "sent" : "draft",
    };

    try {
      setSaving(true);
      if (isEdit) {
        await apiClient.put(`/sa/service-invoices/${id}`, payload);
        setToast({ show: true, message: "Invoice updated.", type: "success" });
      } else {
        await apiClient.post("/sa/service-invoices", payload);
        setToast({ show: true, message: sendImmediately ? "Invoice created & sent." : "Invoice saved as draft.", type: "success" });
      }
      setTimeout(() => navigate("/sa/service-invoices"), 800);
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to save invoice.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewPdf = async () => {
    if (!isEdit) {
      setToast({ show: true, message: "Save the invoice first to preview PDF.", type: "info" });
      return;
    }
    try {
      const response = await apiClient.get(`/sa/service-invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      setToast({ show: true, message: "Failed to generate PDF.", type: "error" });
    }
  };

  const handleSaveAndPreview = async () => {
    const error = validate();
    if (error) {
      setToast({ show: true, message: error, type: "error" });
      return;
    }
    const payload = {
      ...form,
      lineItems: form.lineItems.map((li) => ({
        description: li.description,
        quantity: Number(li.quantity),
        unitPrice: Number(li.unitPrice),
      })),
      status: "draft",
    };
    try {
      setSaving(true);
      const { data } = await apiClient.post("/sa/service-invoices", payload);
      const invoiceId = data.invoice?._id || data._id;
      setDraftInvoiceId(invoiceId);
      // Fetch PDF for preview
      const pdfRes = await apiClient.get(`/sa/service-invoices/${invoiceId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([pdfRes.data], { type: "application/pdf" }));
      setPreviewPdfUrl(url);
      setShowSendPreview(true);
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to save invoice.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmSend = async () => {
    if (!draftInvoiceId) return;
    try {
      setSending(true);
      await apiClient.patch(`/sa/service-invoices/${draftInvoiceId}/send`);
      setShowSendPreview(false);
      if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
      setToast({ show: true, message: "Invoice sent successfully.", type: "success" });
      setTimeout(() => navigate("/sa/service-invoices"), 800);
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to send invoice.", type: "error" });
    } finally {
      setSending(false);
    }
  };

  const handleCancelPreview = () => {
    setShowSendPreview(false);
    if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
    setPreviewPdfUrl(null);
    // Invoice stays as draft — redirect to edit it
    if (draftInvoiceId) {
      navigate(`/sa/service-invoices/${draftInvoiceId}/edit`);
    }
  };

  if (loading) {
    return <div className="si-page"><div className="si-loading">Loading...</div></div>;
  }

  const showEditWarning = isEdit && existingInvoice &&
    (existingInvoice.status === "sent" || existingInvoice.status === "viewed");

  return (
    <div className="si-page">
      <PageHeader
        icon={FileText}
        title={isEdit ? "Edit Invoice" : "Create Service Invoice"}
        description={isEdit ? `Editing ${existingInvoice?.invoiceNumber || ""}` : "Create a new service invoice for an institute"}
        actions={<BackButton />}
      />

      {showEditWarning && (
        <div className="si-edit-warning">
          <span>⚠️ This invoice has already been sent to the institute. Editing will increment the version and notify them.</span>
        </div>
      )}

      <div className="si-form-card">
        {/* Institute Selection */}
        <div className="si-form-row">
          <div className="si-form-group si-form-half">
            <label>Institute *</label>
            <Form.Select
              name="instituteId"
              value={form.instituteId}
              onChange={handleChange}
              disabled={isEdit}
            >
              <option value="">Select Institute</option>
              {institutes.map((inst) => (
                <option key={inst._id} value={inst._id}>
                  {inst.name}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="si-form-group si-form-half">
            <label>Service Type *</label>
            <Form.Select
              name="serviceType"
              value={form.serviceType}
              onChange={handleChange}
            >
              {SERVICE_TYPES.map((st) => (
                <option key={st.value} value={st.value}>
                  {st.label}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>

        <div className="si-form-row">
          <div className="si-form-group si-form-half">
            <label>Billing Type</label>
            <Form.Select
              name="billingType"
              value={form.billingType}
              onChange={handleChange}
            >
              <option value="one-time">One-time</option>
              <option value="recurring">Recurring</option>
              <option value="milestone">Milestone</option>
            </Form.Select>
          </div>
          <div className="si-form-group si-form-half">
            <label>Due Date *</label>
            <Form.Control
              type="date"
              name="dueDate"
              value={form.dueDate}
              onChange={handleChange}
              min={new Date().toISOString().slice(0, 10)}
            />
          </div>
          <div className="si-form-group si-form-half">
            <label>Service Request ID (optional)</label>
            <Form.Control
              type="text"
              name="serviceRequestId"
              value={form.serviceRequestId}
              onChange={handleChange}
              placeholder="Link to service request"
            />
          </div>
        </div>

        {/* Line Items */}
        <div className="si-line-items-section">
          <div className="si-line-items-header">
            <h3>Line Items</h3>
            <AppButton variant="outline" size="sm" onClick={addLineItem}>
              <Plus size={14} /> Add Item
            </AppButton>
          </div>

          <div className="si-line-items-table">
            <div className="si-li-header">
              <span className="si-li-desc">Description</span>
              <span className="si-li-qty">Qty</span>
              <span className="si-li-price">Unit Price (₹)</span>
              <span className="si-li-amount">Amount</span>
              <span className="si-li-action"></span>
            </div>
            {form.lineItems.map((item, idx) => (
              <div className="si-li-row" key={idx}>
                <div className="si-li-desc">
                  <Form.Control
                    type="text"
                    value={item.description}
                    onChange={(e) => handleLineItemChange(idx, "description", e.target.value)}
                    placeholder="Item description"
                  />
                </div>
                <div className="si-li-qty">
                  <Form.Control
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleLineItemChange(idx, "quantity", e.target.value)}
                    min={1}
                  />
                </div>
                <div className="si-li-price">
                  <Form.Control
                    type="number"
                    value={item.unitPrice}
                    onChange={(e) => handleLineItemChange(idx, "unitPrice", e.target.value)}
                    min={0}
                  />
                </div>
                <div className="si-li-amount">
                  ₹{((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0)).toLocaleString("en-IN")}
                </div>
                <div className="si-li-action">
                  {form.lineItems.length > 1 && (
                    <button className="si-remove-btn" onClick={() => removeLineItem(idx)}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="si-total-row">
            <span>Subtotal:</span>
            <span className="si-total-amount">₹{totalAmount.toLocaleString("en-IN")}</span>
          </div>
          {gstEnabled && gstRate > 0 && (
            <>
              <div className="si-total-row si-gst-row">
                <span>CGST @ {halfRate}%:</span>
                <span className="si-total-amount">₹{(totalAmount * halfRate / 100).toFixed(2)}</span>
              </div>
              <div className="si-total-row si-gst-row">
                <span>SGST @ {halfRate}%:</span>
                <span className="si-total-amount">₹{(totalAmount * halfRate / 100).toFixed(2)}</span>
              </div>
              <div className="si-total-row si-gst-row">
                <span>Total GST:</span>
                <span className="si-total-amount">₹{gstAmount.toFixed(2)}</span>
              </div>
            </>
          )}
          {roundedOff !== 0 && (
            <div className="si-total-row si-gst-row">
              <span>Rounded Off:</span>
              <span className="si-total-amount">{roundedOff > 0 ? "+" : ""}₹{roundedOff.toFixed(2)}</span>
            </div>
          )}
          <div className="si-total-row si-grand-total-row">
            <span>Total Invoice Amount:</span>
            <span className="si-total-amount">₹{grandTotal.toLocaleString("en-IN")}</span>
          </div>
        </div>

        {/* Notes */}
        <div className="si-form-group">
          <label>Notes (optional)</label>
          <Form.Control
            as="textarea"
            rows={3}
            name="notes"
            value={form.notes}
            onChange={handleChange}
            placeholder="Any additional notes for the institute..."
          />
        </div>

        {/* Form Actions */}
        <div className="si-form-actions">
          <AppButton variant="outline" onClick={() => navigate("/sa/service-invoices")}>
            Cancel
          </AppButton>
          {isEdit && (
            <AppButton variant="outline" onClick={handlePreviewPdf}>
              <Eye size={16} /> Preview PDF
            </AppButton>
          )}
          <AppButton variant="secondary" onClick={() => handleSubmit(false)} disabled={saving}>
            <Save size={16} /> {isEdit ? "Update" : "Save as Draft"}
          </AppButton>
          {!isEdit && (
            <AppButton onClick={handleSaveAndPreview} disabled={saving}>
              <Send size={16} /> Save & Send
            </AppButton>
          )}
        </div>
      </div>

      {/* Send Preview Modal */}
      <Modal
        show={showSendPreview}
        onHide={handleCancelPreview}
        size="lg"
        centered
        dialogClassName="si-preview-modal"
      >
        <Modal.Header className="si-preview-modal-header">
          <Modal.Title>Review Invoice Before Sending</Modal.Title>
          <button className="si-preview-close" onClick={handleCancelPreview}>
            <X size={18} />
          </button>
        </Modal.Header>
        <Modal.Body className="si-preview-modal-body">
          {previewPdfUrl ? (
            <iframe
              src={previewPdfUrl}
              title="Invoice Preview"
              className="si-preview-iframe"
            />
          ) : (
            <div className="si-preview-loading">Loading preview...</div>
          )}
        </Modal.Body>
        <Modal.Footer className="si-preview-modal-footer">
          <AppButton variant="outline" onClick={handleCancelPreview} disabled={sending}>
            Keep as Draft
          </AppButton>
          <AppButton onClick={handleConfirmSend} disabled={sending}>
            <Send size={16} /> {sending ? "Sending..." : "Confirm & Send"}
          </AppButton>
        </Modal.Footer>
      </Modal>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: "", type: "success" })}
      />
    </div>
  );
};

export default ServiceInvoiceForm;
